interface PopoverPositionerOptions {
  offset?: number;
  preferredPosition?: Position;
  boundaryPadding?: number;
}

interface Viewport {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

interface TriggerBounds {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

interface PopoverDimensions {
  width: number;
  height: number;
}

interface Position2D {
  x: number;
  y: number;
}

interface PositionResult {
  position: Position;
  x: number;
  y: number;
}

interface ViewportFit {
  fits: boolean;
  position: Position2D;
}

interface PositionFits {
  [key: string]: ViewportFit;
}

interface Positions {
  [key: string]: Position2D;
}

type Position = 'top' | 'bottom' | 'left' | 'right';

class PopoverPositioner {
  private offset: number;
  private preferredPosition: Position;
  private boundaryPadding: number;

  constructor(options: PopoverPositionerOptions = {}) {
    this.offset = options.offset || 10;
    this.preferredPosition = options.preferredPosition || 'bottom';
    this.boundaryPadding = options.boundaryPadding || 10;
  }

  /**
   * Position a popover relative to a trigger element
   */
  position(popover: HTMLElement, trigger: HTMLElement, options: PopoverPositionerOptions = {}): PositionResult {
    const config: Required<PopoverPositionerOptions> = { ...this, ...options };

    const viewport: Viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.pageXOffset || document.documentElement.scrollLeft,
      scrollY: window.pageYOffset || document.documentElement.scrollTop,
    };

    const triggerRect = trigger.getBoundingClientRect();
    const triggerBounds: TriggerBounds = {
      top: triggerRect.top + viewport.scrollY,
      left: triggerRect.left + viewport.scrollX,
      right: triggerRect.right + viewport.scrollX,
      bottom: triggerRect.bottom + viewport.scrollY,
      width: triggerRect.width,
      height: triggerRect.height,
      centerX: triggerRect.left + viewport.scrollX + triggerRect.width / 2,
      centerY: triggerRect.top + viewport.scrollY + triggerRect.height / 2,
    };

    const originalDisplay = popover.style.display;
    const originalVisibility = popover.style.visibility;
    popover.style.display = 'block';
    popover.style.visibility = 'hidden';
    popover.style.position = 'absolute';

    const popoverRect = popover.getBoundingClientRect();
    const popoverDimensions: PopoverDimensions = {
      width: popoverRect.width,
      height: popoverRect.height,
    };

    const positions = this._calculatePositions(triggerBounds, popoverDimensions, config.offset);
    const fitsInViewport = this._checkViewportFit(positions, popoverDimensions, viewport, config.boundaryPadding);
    const bestPosition = this._chooseBestPosition(config.preferredPosition, fitsInViewport);
    const finalPosition = positions[bestPosition];

    popover.style.left = finalPosition.x + 'px';
    popover.style.top = finalPosition.y + 'px';
    popover.style.display = originalDisplay;
    popover.style.visibility = originalVisibility;

    popover.classList.remove('popover-top', 'popover-bottom', 'popover-left', 'popover-right');
    popover.classList.add(`popover-${bestPosition}`);

    return {
      position: bestPosition,
      x: finalPosition.x,
      y: finalPosition.y,
    };
  }

  private _calculatePositions(triggerBounds: TriggerBounds, popoverDimensions: PopoverDimensions, offset: number): Positions {
    return {
      top: {
        x: triggerBounds.centerX - popoverDimensions.width / 2,
        y: triggerBounds.top - popoverDimensions.height - offset,
      },
      bottom: {
        x: triggerBounds.centerX - popoverDimensions.width / 2,
        y: triggerBounds.bottom + offset,
      },
      left: {
        x: triggerBounds.left - popoverDimensions.width - offset,
        y: triggerBounds.centerY - popoverDimensions.height / 2,
      },
      right: {
        x: triggerBounds.right + offset,
        y: triggerBounds.centerY - popoverDimensions.height / 2,
      },
    };
  }

  private _checkViewportFit(
    positions: Positions,
    popoverDimensions: PopoverDimensions,
    viewport: Viewport,
    boundaryPadding: number
  ): PositionFits {
    const fits: PositionFits = {};

    for (const [side, pos] of Object.entries(positions)) {
      const adjustedPos = this._adjustForViewport(pos, popoverDimensions, viewport, boundaryPadding);
      fits[side] = {
        fits: adjustedPos.fits,
        position: adjustedPos.position,
      };
      positions[side] = adjustedPos.position;
    }

    return fits;
  }

  private _adjustForViewport(
    position: Position2D,
    dimensions: PopoverDimensions,
    viewport: Viewport,
    padding: number
  ): { fits: boolean; position: Position2D } {
    let { x, y } = position;
    let fits = true;

    if (x < viewport.scrollX + padding) {
      x = viewport.scrollX + padding;
      fits = false;
    } else if (x + dimensions.width > viewport.scrollX + viewport.width - padding) {
      x = viewport.scrollX + viewport.width - dimensions.width - padding;
      fits = false;
    }

    if (y < viewport.scrollY + padding) {
      y = viewport.scrollY + padding;
      fits = false;
    } else if (y + dimensions.height > viewport.scrollY + viewport.height - padding) {
      y = viewport.scrollY + viewport.height - dimensions.height - padding;
      fits = false;
    }

    return {
      fits,
      position: { x, y },
    };
  }

  private _chooseBestPosition(preferred: Position, fitsInViewport: PositionFits): Position {
    if (fitsInViewport[preferred]?.fits) {
      return preferred;
    }

    const priorityOrder: Position[] = ['bottom', 'top', 'right', 'left'];

    for (const position of priorityOrder) {
      if (fitsInViewport[position]?.fits) {
        return position;
      }
    }

    return preferred;
  }

  /**
   * Update popover position
   */
  update(popover: HTMLElement, trigger: HTMLElement, options: PopoverPositionerOptions = {}): PositionResult {
    return this.position(popover, trigger, options);
  }

  /**
   * Auto-update popover position on scroll and resize
   */
  autoUpdate(popover: HTMLElement, trigger: HTMLElement, options: PopoverPositionerOptions = {}): () => void {
    const updatePosition = (): void => {
      this.update(popover, trigger, options);
    };

    let ticking = false;
    const throttledUpdate = (): void => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updatePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledUpdate);
    window.addEventListener('resize', throttledUpdate);

    return (): void => {
      window.removeEventListener('scroll', throttledUpdate);
      window.removeEventListener('resize', throttledUpdate);
    };
  }
}

export default PopoverPositioner;
